/** Tasks — v1 tasksView parity: page header + 4 stat tiles (Pending / In
 * Progress / Completed / Overdue), a filter row (search / priority / assignee
 * / property / status), and the list grouped into Properties / Transactions /
 * Wholesale sections (v1 taskCategory) with a coloured priority dot per row.
 * Rows still expand into the who / due / priority editor (opPatchTask keys).
 * Categorisation reuses app.tsx's dealKeys / wholesaleKeys nat-key sets. */
import { useMemo, useState } from "react";
import { isTaskDone, type TaskRec } from "../edits/api";
import { fmtDateEdit, toDateInput, parseDealDate } from "../deals/api";
import { daysFromTodayAppTz } from "../../lib/apptime";
import { natKey as natKeyOf } from "../../lib/natkey";
import { useConfirmDelete } from "../common/ConfirmDelete";

/** v1 priority vocabulary (priDot/priC maps): High / Medium / Low. */
export const PRIORITIES = ["High", "Medium", "Low"];

/** "Jul 20, 2026" (v1 display string) → epoch ms, or NaN when unset/unparseable.
 * Goes through parseDealDate so a bare ISO due date isn't read as UTC midnight
 * — that made a task due today look overdue from 8pm the night before. */
function dueMs(due: string | null | undefined): number {
  const d = parseDealDate(due);
  return d ? d.getTime() : NaN;
}

/** "Overdue" means the due DATE is before today in Eastern — not before this
 * instant. A task due today is not overdue at 9am, and a device in another
 * timezone must not disagree about which day it is. */
function isOverdue(due: string | null | undefined, now: Date = new Date()): boolean {
  const d = parseDealDate(due);
  return d ? daysFromTodayAppTz(d, now) < 0 : false;
}

type Cat = "property" | "transaction" | "wholesale";

export function TasksPage({
  tasks,
  members,
  dealKeys,
  wholesaleKeys,
  onAdd,
  onToggle,
  onRemove,
  onPatch,
}: {
  tasks: TaskRec[];
  /** team display names (from the profiles roster) — datalist suggestions */
  members: string[];
  /** nat-keys of deals / wholesale deals — drives v1's taskCategory grouping */
  dealKeys: Set<string>;
  wholesaleKeys: Set<string>;
  onAdd: (title: string, opts?: { deal?: string; due?: string; who?: string; pri?: string; status?: string }) => void;
  onToggle: (key: string, done: boolean) => void;
  onRemove: (key: string) => void;
  onPatch: (key: string, patch: { who?: string; due?: string; pri?: string }) => void;
}) {
  const [title, setTitle] = useState("");
  const [due, setDue] = useState("");
  const [who, setWho] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const confirmDel = useConfirmDelete(); // v1 deleteTask — typed-delete gated
  // properties/deals that already have tasks — datalist for the Create Task modal
  const propertyOptions = [...new Set(tasks.map((t) => t.deal).filter((d): d is string => !!d))].sort();

  // v1 filter controls
  const [q, setQ] = useState("");
  const [fPri, setFPri] = useState("");
  const [fWho, setFWho] = useState("");
  const [fDeal, setFDeal] = useState("");
  // "overdue" added 2026-07-29: the stat tiles are now the status filter, and
  // Overdue is the one people actually act on. It was previously visible as a
  // COUNT with no way to filter to it.
  const [fStatus, setFStatus] = useState<"" | "pending" | "completed" | "overdue">("pending"); // v1 default: show open work, not everything
  const [addOpen, setAddOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters = (fPri ? 1 : 0) + (fWho ? 1 : 0) + (fDeal ? 1 : 0);

  // v1 taskCategory: wholesale if the deal is a wholesale deal; transaction if
  // it's an auto/stage checklist task or the deal is a flip; else property.
  const category = (t: TaskRec): Cat => {
    const k = t.deal ? natKeyOf(t.deal) : "";
    if (k && wholesaleKeys.has(k)) return "wholesale";
    if (t.auto || t.stage) return "transaction";
    if (k && dealKeys.has(k)) return "transaction";
    return "property";
  };

  const now = Date.now();
  const stats = useMemo(() => {
    let pending = 0, prog = 0, done = 0, overdue = 0;
    for (const t of tasks) {
      const isDone = isTaskDone(t.status);
      if (isDone) { done++; continue; }
      if (t.status === "in_progress") prog++;
      else pending++;
      if (isOverdue(t.due)) overdue++;
    }
    return { pending, prog, done, overdue };
  }, [tasks, now]);

  // distinct assignees / properties for the filter selects
  const whoOpts = useMemo(() => {
    const s = new Set<string>();
    for (const t of tasks) for (const w of String(t.who ?? "").split(",").map((x) => x.trim())) if (w) s.add(w);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [tasks]);
  const dealOpts = useMemo(() => {
    const s = new Set<string>();
    for (const t of tasks) if (t.deal) s.add(t.deal);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return tasks.filter((t) => {
      if (fStatus === "pending" && isTaskDone(t.status)) return false;
      if (fStatus === "completed" && !isTaskDone(t.status)) return false;
      if (fStatus === "overdue" && !(!isTaskDone(t.status) && isOverdue(t.due))) return false;
      if (fPri && (t.pri ?? "") !== fPri) return false;
      if (fWho && !String(t.who ?? "").split(",").map((x) => x.trim()).includes(fWho)) return false;
      if (fDeal && (t.deal ?? "") !== fDeal) return false;
      if (needle) {
        const hay = `${t.title} ${t.deal ?? ""} ${t.who ?? ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [tasks, q, fPri, fWho, fDeal, fStatus]);

  const groups: { cat: Cat; label: string; icon: string; rows: TaskRec[] }[] = useMemo(() => {
    const defs: { cat: Cat; label: string; icon: string }[] = [
      { cat: "property", label: "Properties", icon: "🏠" },
      { cat: "transaction", label: "Transactions", icon: "🔁" },
      { cat: "wholesale", label: "Wholesale Transactions", icon: "🤝" },
    ];
    return defs.map((d) => ({ ...d, rows: filtered.filter((t) => category(t) === d.cat) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, dealKeys, wholesaleKeys]);

  const add = () => {
    if (!title.trim()) return;
    const opts: { due?: string; who?: string } = {};
    if (due.trim()) opts.due = due.trim();
    if (who.trim()) opts.who = who.trim();
    onAdd(title, opts);
    setTitle("");
    setDue("");
    setWho("");
  };

  const row = (t: TaskRec) => {
    const isEditing = editing === t.natKey;
    const pri = (t.pri ?? "").toLowerCase();
    const overdue = !isTaskDone(t.status) && isOverdue(t.due);
    return (
      <div key={t.natKey} className={"taskrow" + (isEditing ? " editing" : "")}>
        <input
          type="checkbox"
          checked={isTaskDone(t.status)}
          onChange={(e) => onToggle(t.natKey, e.target.checked)}
          title={isTaskDone(t.status) ? "Reopen" : "Mark done"}
        />
        <span className={"pridot " + (pri || "none")} title={t.pri ? t.pri + " priority" : "No priority"} />
        <div className="taskbody" onClick={() => setEditing(isEditing ? null : t.natKey)}>
          <span className={"tasktitle" + (isTaskDone(t.status) ? " donetext" : "")}>
            {t.auto ? <span className="lbadge boff" title={"Stage checklist" + (t.stage ? ": " + t.stage : "")}>auto</span> : null} {t.title}
          </span>
          <span className="sub">
            {t.deal ? `${t.deal} · ` : ""}
            {t.due ? <em className={overdue ? "overdue" : ""}>due {t.due}</em> : "no due date"}
            {t.who ? ` · ${t.who}` : ""}
          </span>
        </div>
        {t.pri ? <span className={"pri pri-" + pri}>{pri}</span> : null}
        <button className="taskdel" title="Delete task" onClick={() => confirmDel.ask("Delete this task?", () => onRemove(t.natKey))}>×</button>
        {isEditing ? (
          <div className="taskedit" onClick={(e) => e.stopPropagation()}>
            <div className="arvfield">
              <label className="lbl">Assign to</label>
              <input
                className="moneyinput"
                list="team-names"
                defaultValue={t.who ?? ""}
                placeholder="e.g. Tom"
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (t.who ?? "")) onPatch(t.natKey, { who: v });
                }}
                onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
              />
            </div>
            <div className="arvfield">
              <label className="lbl">Due date</label>
              <input
                className="moneyinput"
                type="date"
                defaultValue={toDateInput(t.due)}
                onChange={(e) => onPatch(t.natKey, { due: e.target.value ? fmtDateEdit(e.target.value) : "" })}
              />
            </div>
            <div className="arvfield">
              <label className="lbl">Priority</label>
              <select
                className="moneyinput"
                value={t.pri ?? ""}
                onChange={(e) => onPatch(t.natKey, { pri: e.target.value })}
              >
                <option value="">—</option>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
                {t.pri && !PRIORITIES.includes(t.pri) ? <option value={t.pri}>{t.pri}</option> : null}
              </select>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const anyRows = groups.some((g) => g.rows.length > 0);

  return (
    <div className="tasks">
      <div className="taskhead">
        <div>
          <h1>Tasks</h1>
          <span className="sub">
            {tasks.length.toLocaleString()} total task{tasks.length === 1 ? "" : "s"}
            {/* phone-only (CSS): the In-Progress chip is hidden there, and its
                count has to live SOMEWHERE — a number may not just vanish */}
            {stats.prog ? <span className="subprog"> · {stats.prog} in progress</span> : null}
          </span>
        </div>
      </div>

      {/* The four tiles and the status dropdown were the same information twice
          — "26 Pending" as a card AND "Pending" as a select. They are one thing
          now: tap a tile to filter to it. On a phone they collapse to a single
          scrolling row of chips, which is where ~200px of the old layout went. */}
      <div className="tstats">
        <button type="button" className={"tstat blue" + (fStatus === "pending" ? " on" : "")} aria-pressed={fStatus === "pending"} onClick={() => setFStatus(fStatus === "pending" ? "" : "pending")}>
          <span className="tstatico">◔</span><div><strong>{stats.pending}</strong><span>Pending</span></div>
        </button>
        <button type="button" className="tstat amber" disabled title="No separate in-progress filter yet">
          <span className="tstatico">▶</span><div><strong>{stats.prog}</strong><span>In Progress</span></div>
        </button>
        <button type="button" className={"tstat emerald" + (fStatus === "completed" ? " on" : "")} aria-pressed={fStatus === "completed"} onClick={() => setFStatus(fStatus === "completed" ? "" : "completed")}>
          {/* "Done" on phones, "Completed" on desktop — CSS swaps the spans.
              Three chips share 390px; a nine-letter label is the one that
              would push the row back into scrolling at three-digit counts. */}
          <span className="tstatico">✓</span><div><strong>{stats.done}</strong><span className="tlblfull">Completed</span><span className="tlblshort">Done</span></div>
        </button>
        <button type="button" className={"tstat red" + (fStatus === "overdue" ? " on" : "")} aria-pressed={fStatus === "overdue"} onClick={() => setFStatus(fStatus === "overdue" ? "" : "overdue")}>
          <span className="tstatico">!</span><div><strong>{stats.overdue}</strong><span>Overdue</span></div>
        </button>
      </div>

      {/* Permanently-expanded create form cost ~130px for something done
          occasionally. On a phone it is a button until you want it. */}
      <button type="button" className="taskaddbtn" onClick={() => setAddOpen((v) => !v)} aria-expanded={addOpen}>
        + Add task
      </button>
      <header className={"bar taskaddbar" + (addOpen ? " open" : "")}>
        <input
          placeholder="New task — what needs doing?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { if (due.trim() || who.trim()) add(); else setCreateOpen(true); } }}
        />
        <input className="short" placeholder="Due (e.g. 2026-07-20)" value={due} onChange={(e) => setDue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <input className="short" list="team-names" placeholder="Who" value={who} onChange={(e) => setWho(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} />
        <datalist id="team-names">
          {members.map((m) => (
            <option key={m} value={m} />
          ))}
        </datalist>
        <button className="btn primary" onClick={() => setCreateOpen(true)}>+ Add Task</button>
      </header>

      {createOpen ? (
        <CreateTaskModal
          initialTitle={title}
          members={members}
          propertyOptions={propertyOptions}
          onCreate={(t, opts) => { onAdd(t, opts); setTitle(""); setDue(""); setWho(""); }}
          onClose={() => setCreateOpen(false)}
        />
      ) : null}

      <div className="taskfilterrow">
        <input className="tfsearch" placeholder="🔍 Search tasks, deals, people…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className={"barfilterbtn" + (activeFilters ? " on" : "")} onClick={() => setFiltersOpen(true)}>
          Filters{activeFilters ? <span className="barfiltercount">{activeFilters}</span> : null}
        </button>
      </div>
      {filtersOpen ? <div className="barfilterscrim" onClick={() => setFiltersOpen(false)} /> : null}
      <div className={"taskfilters" + (filtersOpen ? " open" : "")}>
        <div className="barfiltershd"><span>Filters</span><button type="button" className="barfilterdone" onClick={() => setFiltersOpen(false)}>Done</button></div>
        <select value={fPri} onChange={(e) => setFPri(e.target.value)}>
          <option value="">All priorities</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={fWho} onChange={(e) => setFWho(e.target.value)}>
          <option value="">All users</option>
          {whoOpts.map((w) => <option key={w} value={w}>{w}</option>)}
        </select>
        <select value={fDeal} onChange={(e) => setFDeal(e.target.value)}>
          <option value="">All properties</option>
          {dealOpts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        {activeFilters ? (
          <button type="button" className="barfilterclear" onClick={() => { setFPri(""); setFWho(""); setFDeal(""); }}>Clear all filters</button>
        ) : null}
      </div>

      {!anyRows ? <div className="empty">No tasks match.</div> : null}
      {groups.map((g) =>
        g.rows.length === 0 ? null : (
          <div key={g.cat} className="taskgroup">
            <div className="tgh">
              <span className="tghico">{g.icon}</span>
              <h3>{g.label}</h3>
              <span className="tghcount">{g.rows.length}</span>
            </div>
            <div className="tasklist">{g.rows.map(row)}</div>
          </div>
        ),
      )}
      {confirmDel.modal}
    </div>
  );
}

/** v1 Create Task modal — full form (task, property, assignees, due, priority,
 * status) opened from the "+ Add Task" button, replacing the cramped inline row. */
function CreateTaskModal({ initialTitle, members, propertyOptions, onCreate, onClose }: {
  initialTitle: string;
  members: string[];
  propertyOptions: string[];
  onCreate: (title: string, opts: { deal?: string; due?: string; who?: string; pri?: string; status?: string }) => void;
  onClose: () => void;
}) {
  const [title, setTitle] = useState(initialTitle);
  const [deal, setDeal] = useState("");
  const [assignees, setAssignees] = useState<string[]>([]);
  const [due, setDue] = useState("");
  const [pri, setPri] = useState("Medium");
  const [status, setStatus] = useState("pending");
  const submit = () => {
    const t = title.trim();
    if (!t) return;
    const opts: { deal?: string; due?: string; who?: string; pri?: string; status?: string } = { pri, status };
    if (deal.trim()) opts.deal = deal.trim();
    if (due.trim()) opts.due = due.trim();
    if (assignees.length) opts.who = assignees.join(", ");
    onCreate(t, opts);
    onClose();
  };
  return (
    <div className="cmpmodalscrim" onClick={onClose}>
      <div className="cmpmodal taskmodal" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody">
          <div className="cmpmodalhead"><h3>Create Task</h3><button className="close" onClick={onClose}>×</button></div>
          <label className="lbl">Task</label>
          <input className="moneyinput" autoFocus value={title} placeholder="What needs doing?" onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          <label className="lbl">Deal / Property</label>
          <input className="moneyinput" list="task-props" value={deal} placeholder="Choose an existing property or type one" onChange={(e) => setDeal(e.target.value)} />
          <datalist id="task-props">{propertyOptions.map((p) => <option key={p} value={p} />)}</datalist>
          <div className="taskmodalrow">
            <div className="arvfield">
              <label className="lbl">Assign to (pick several)</label>
              <select multiple className="taskassign" value={assignees} onChange={(e) => setAssignees(Array.from(e.target.selectedOptions).map((o) => o.value))}>
                {members.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="arvfield"><label className="lbl">Due date</label><input className="moneyinput" type="date" value={due} onChange={(e) => setDue(e.target.value)} /></div>
          </div>
          <div className="taskmodalrow">
            <div className="arvfield"><label className="lbl">Priority</label><select className="moneyinput" value={pri} onChange={(e) => setPri(e.target.value)}><option>Low</option><option>Medium</option><option>High</option></select></div>
            <div className="arvfield"><label className="lbl">Status</label><select className="moneyinput" value={status} onChange={(e) => setStatus(e.target.value)}><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option></select></div>
          </div>
          <div className="expformacts" style={{ marginTop: 14 }}>
            <button className="btn slim" onClick={onClose}>Cancel</button>
            <button className="btn slim primary" onClick={submit}>Create Task</button>
          </div>
        </div>
      </div>
    </div>
  );
}
